// Tuning constants ported from the reference. Units are Unreal centimetres, Z up.
// Each value cites its source in SPEC.md terms (`P/` = reference/Plugin_Source/PerpCity/).

/** Tile edge length. P/WorldManager.h:58 */
export const TILE_SIZE = 25000;

export const TERRAIN = {
  /** P/TerrainTile.h:164-176 */
  octaves: 10,
  vertScale: 10000,
  vertOffset: -6000,
  frequency: 0.4,
  amplitude: 1,
  lacunarity: 2,
  persistence: 0.5,
  /** Mountain threshold on ridge/denom and its gain. P/TerrainTile.cpp:259-263 */
  ridgeThreshold: 0.7,
  ridgeGain: 20,
  flatScale: 0.75,
  /** Extra grid steps outside each tile edge, lowered as a skirt. P/TerrainTile.cpp:497, 334-350 */
  seamOverlapSteps: 2,
  skirtSlope: 1,
  /** Highest LOD the tile manager uses (P/WorldManager.cpp:73 starts at 6 and decrements before use). */
  maxLod: 5,
} as const;

/** Cells per tile edge at a LOD: 2^(lod+2). P/TerrainTile.cpp:493 (exact division; see DECISIONS). */
export function lodCells(lod: number): number {
  return 2 ** (lod + 2);
}

export const SLOPE = {
  /** P/TerrainTile.cpp:576-577, 1933 */
  maxBuildableSlope: 0.15,
  /** Analysis grid = LOD1 grid. P/TerrainTile.cpp:540-544 */
  analysisLod: 1,
} as const;

export const ROADS = {
  /** P/TerrainTile.h:186-214 */
  genLimit: 400,
  primaryStep: 8000,
  secondaryStep: 4500,
  primaryChangeIntensity: 35,
  secondaryChangeIntensity: 15,
  switchToSmallerRoadPercent: 0.75,
  standardWidth: 1190,
  /** Branch noise. P/TerrainTile.cpp:1288-1290 */
  noiseOffsetX: 8634,
  noiseOffsetY: 9763,
  noiseScale: 0.0004,
  noiseFrequency: 100.04,
  /** Touch tolerance (OnSegment). P/TerrainTile.cpp:1552 */
  touchTolerance: 10,
  /** Candidate discarded when its nearest non-touching road is closer than this. :660 */
  discardDistance: 10,
  /** Endpoint merge tolerance. SnapRoadEndsTogether :1070 */
  nodeSnap: 800,
  /** Snap an endpoint onto the middle of an existing road. roadSnapDistance, P/TerrainTile.h:190 */
  edgeSnap: 300,
  /** Score scale: score = len * touched * (nearest/10000)^2. :1277 */
  scoreDistanceScale: 10000,
  /** Seed road side candidates: exclude this many grid points at each corner. :1858-1877 */
  gateCornerExclusion: 1,
  /** Our own: gates per tile edge (the reference has one per side) and their minimum spacing in LOD1 steps. */
  gatesPerEdge: 2,
  gateMinSpacing: 3,
  /** Our own dead-end closing pass: dead ends this close to the tile border try to reach a gate within
   * gateReach, else any road within edgeReach, turning at most maxTurn degrees and keeping clearance
   * from unrelated roads. */
  borderReach: 6000,
  gateReach: 9000,
  edgeReach: 5000,
  maxTurn: 80,
  clearance: 1500,
} as const;

/** Lot subdivision (ASpawnDistrict::splitBigBuildings, P/SpawnDistrict.cpp:2678-2829; SPEC §3). */
export const LOTS = {
  /** Split a plot if area > maxArea m², or its longest side > maxLongest cm and area > maxAreaLong m². :2697 */
  maxArea: 8000,
  maxAreaLong: 5000,
  maxLongest: 22000,
  /** Recursion limit. :2811 */
  maxDepth: 4,
  /** Our own: width of the paved gap left between lots (the reference cuts along a line). */
  gap: 600,
} as const;

export const BLOCKS = {
  /** Inset then outset for sidewalk and footprint. P/SpawnBuilding.cpp:345-346 */
  roadInset: 1200,
  roadOutset: 600,
  /** Minimum plot area in m^2. P/SpawnBuilding.cpp:272 */
  minPlotArea: 280,
  /** Road surface grown by this under blocks. P/SpawnBuilding.cpp:2407 */
  roadGrow: 10,
  roadZ: -20,
  curbHeight: 20,
  /** Draped heights above the terrain (our own): road surface, and sidewalk = road + curb. */
  roadLift: 25,
  sidewalkLift: 45,
  /** Our own: grid cell (cm) for draping roads, sidewalks and parks; coarser on imposter tiles. */
  drapeCell: 400,
  drapeCellFar: 1600,
  /** Our own cap: faces larger than this (m^2) are treated as open land, not city blocks. */
  maxBlockArea: 50000,
} as const;

export const BUILDING = {
  /** P/SpawnBuilding.h */
  floorHeight: 500,
  baseFloorTop: 2,
  /** Elevator shaft. P/SpawnBuilding.cpp:2919-2924 */
  stepWidth: 140,
  elevatorWidth: 560,
  elevatorDepth: 680,
  wallThickness: 20,
  /** Residential rule. P/SpawnBuilding.cpp:2854 */
  residentialMinLongest: 580 * 4,
  residentialMinSkinny: 700,
  residentialMaxSkinny: 4500,
  /** Low-rise vs park split. P/SpawnBuilding.cpp:2878-2886 */
  lowRiseInset: 800,
  lowRiseOutset: 400,
  lowRiseChance: 40,
  parkInset: 300,
  /** Tower floors, from the legacy district rule. P/SpawnDistrict.cpp:844-851 */
  minFloors: 3,
  maxFloors: 60,
  /** Floor evolution. P/SpawnBuilding.cpp:957-1100 */
  simplicity: 4,
  /** Window rhythm on exterior wall lines. P/SpawnBuilding.cpp:3122-3123 */
  windowEdgeWidth: 36,
  windowWidth: 500 - 36,
  /** Pillars. P/SpawnBuilding.cpp:3012-3040 */
  pillarWidth: 200,
  dividerHeight: 60,
  /** Lobby. P/SpawnBuilding.cpp:3944-4155 */
  lobbyMinWall: 3000,
  lobbyBand: 60,
  lobbyDoorWidth: 140,
  lobbyDoorHeight: 230,
  lobbyDoorGap: 25,
  /** Special buildings. P/SpawnBuilding.cpp:2970-2990 */
  genericMax: 400,
  circleMax: 700,
  circleDegreePerWall: 20,
} as const;

export const LOWRISE = {
  /** P/SpawnLowRise.h:48, P/SpawnBuilding.cpp:678 */
  floorHeight: 290,
  floorCount: 4,
  /** P/SpawnLowRise.cpp:1654-1658 */
  minWidth: 580,
  maxWidth: 700,
  maxLength: 4500,
  /** Facade. P/SpawnLowRise.cpp:1204-1307 */
  windowWidth: 76,
  doorWidth: 106,
  windowBottom: 60,
  windowTop: 220,
  doorTop: 241,
} as const;

export const FLOORPLAN = {
  /** P/SpawnBuildingFloor.h */
  windowBottomHeight: 100,
  windowTopHeight: 195,
  doorTopHeight: 215,
  /** P/SpawnBuildingFloor.cpp:2651-2652 */
  freeWindowDepth: 152,
  freeWindowWidth: 9,
  /** P/SpawnBuildingFloor.cpp:2685 */
  hallwayWidth: 250,
  hallwayReach: 5000,
  /** fillPlotHoles opening/closing and minimum area (m^2). :2520-2522, 2586-2588 */
  holeMorph: 122,
  minRoomArea: 15,
  /** splitBigRooms. :2877-2882, 2982 */
  maxRoomArea: 400,
  maxSplits: 40,
  splitInset: 160,
  /** Final room opening. :2940 */
  finalOpen: 80,
  /** enforceKeepTogethers. :2616, 2625, 2635 */
  keepTogetherPasses: 5,
  keepTogetherGrow: 3,
  keepTogetherClose: 80,
  /** Door links. :489, 564, 632, 656, 660 */
  doorProbe: 10,
  minDoorOverlap: 180,
  hallwayBonus: 2,
  elevatorOpening: 860,
  doorWidth: 140,
  /** Hallway mesh. :1677, 1943 */
  hallwayCeiling: 484,
  hallwayWallInset: 7,
} as const;

/** Our own residential floors (the reference only lays out offices). Lengths in cm, areas in m². */
export const RESIDENTIAL = {
  /** Apartment depth from the facade to the corridor, tried in order until the core holds the shaft. */
  depths: [1000, 850, 700],
  /** Apartment width along the corridor, chosen per building in this range. */
  unitWidth: [1000, 1500],
  minUnitArea: 28,
  /** Entry/kitchen and bathroom strip along the corridor. */
  frontDepth: [260, 420],
  bathWidth: 270,
  bedWidth: 420,
  minLiving: 400,
  /** Snap apartment and room walls to the facade's window piers within this distance. */
  pierSnap: 260,
  doorWidth: 95,
  entranceWidth: 105,
  /** Shared wall length needed for a door (the office rule is 180 cm for 140 cm doors). */
  minDoorOverlap: 130,
  corridorOpening: 400,
  /** Corridor ring width; the core inside it becomes a lift lobby and residents' rooms of at most innerRoomArea. */
  corridorWidth: 240,
  innerRoomArea: 180,
  /** Share of towers that are residential, and mixed-use (offices below `mixedSplit` of the height). */
  residentialShare: 0.3,
  mixedShare: 0.2,
  mixedSplit: 0.4,
} as const;

export const ROOM = {
  /** P/SpawnRoom.h */
  wallThickness: 20,
  minPathWidth: 70,
  windowBottomHeight: 100,
  windowTopHeight: 195,
  doorTopHeight: 205,
  roofHeight: 304,
  wallOutsideShiftIn: 5,
} as const;

export const DECOR = {
  /** P/ProceduralDecorationActor.cpp:1202 */
  tolerance: 3,
  /** Attempts per ingredient. :904 */
  repeatPerIngredient: 5,
  /** Walk strip in front of wall objects. P/SpawnRoom.cpp:2529 */
  walkStrip: 75,
  /** Walkability corridor width. P/SpawnRoom.cpp:2596, P/ProceduralDecorationActor.cpp:1749 */
  walkingWidth: 75,
  /** Ceiling grid. :1116-1117, 1071 */
  ceilingSpacing: 800,
  ceilingMinSpacing: 600,
  ceilingInset: 40,
  /** getSlideLimit. P/Misc.h:643-644 */
  slideMaxTries: 40,
  slideResolution: 2,
  /** Reject placements whose first corner is farther than this from the room origin. :1774 */
  maxDistanceFromOrigin: 1000,
  /** Clipper CleanPolygons default distance. */
  cleanDistance: 1.415,
} as const;

/** Clipper integer scale: 1 unit = 1 mm (the reference truncates to whole cm; see DECISIONS). */
export const CLIPPER_SCALE = 10;
